import { CommerceMedia } from '@api/types';
import {
  GalleryContext,
  GalleryModal,
  Image,
  ProductCampaignChip,
  ProductPackageChip,
} from '@components';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { Box, IconButton, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints, spacing } from '@themes/theme.helpers';
import { useMemo, useState } from 'react';
import { A11y, Autoplay, Navigation, Pagination, Parallax } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

const ImageGalleryBox = styled(Box)`
  position: relative;
  .swiper {
    background: ${colors.background.white};
    height: 590px;
    ${breakpoints.down('md')} {
      height: 400px;
    }
  }
  .swiper-wrapper {
    height: inherit !important;
  }
  img {
    width: 100%;
    height: 100%;
  }
  .swiper-button-next,
  .swiper-button-prev {
    color: ${colors.background.white};
    background-color: ${colors.primary.mainGreen};
    width: 48px;
    height: 48px;
    border-radius: 50%;

    &:after {
      font-size: 14px;
      font-weight: 800;
    }
  }

  .swiper-pagination-bullet {
    &-active {
      background: ${colors.primary.mainGreen};
    }
  }
`;

const ChipWrapper = styled(Box)`
  position: absolute;
  left: ${spacing(2)};
  top: ${spacing(2)};
  display: flex;
  flex-direction: column;
  place-items: flex-start;
  gap: ${spacing(0.5)};
  z-index: 2;
`;
// TYPES
//------
export type GalleryProps = {
  images?: CommerceMedia[];
  packageIncluded?: boolean;
  isCampaignProduct?: boolean;
};

export const Gallery = ({ images = [], packageIncluded, isCampaignProduct }: GalleryProps) => {
  // Gallery state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const galleryContextValue = useMemo(
    () => ({
      images,
      currentImage,
      setCurrentImage,
      isModalOpen,
      setIsModalOpen,
    }),
    [images, currentImage, setCurrentImage, isModalOpen, setIsModalOpen]
  );

  const handleImageClick = (index: number) => {
    setCurrentImage(index);
    setIsModalOpen(true);
  };
  const shouldDisplayChips = packageIncluded || isCampaignProduct;

  const pictureSources = [
    { media: '(max-width:600px)', params: { width: 400, quality: 60, format: 'webp' } } as const,
    { media: '(max-width:960px)', params: { width: 600, quality: 70, format: 'webp' } } as const,
    { params: { width: 800, quality: 85, format: 'webp' } } as const,
  ];

  const imgSizes = '(max-width:600px) 100vw, (max-width:960px) 80vw, 800px';

  return (
    <GalleryContext.Provider value={galleryContextValue}>
      <ImageGalleryBox>
        {shouldDisplayChips ? (
          <ChipWrapper>
            {isCampaignProduct ? <ProductCampaignChip /> : null}
            {packageIncluded ? <ProductPackageChip /> : null}
          </ChipWrapper>
        ) : null}
        <Swiper
          modules={[Navigation, Pagination, A11y, Autoplay, Parallax]}
          navigation
          pagination={{ clickable: true }}
          observer={true}
          observeSlideChildren={true}
          resizeObserver={true}
        >
          {images.map((image, index) => (
            <SwiperSlide key={image.contentLink?.id}>
              <Image
                image={image?.contentLink?.url || ''}
                scalingMode='fit'
                width='800'
                height='590'
                pictureSources={pictureSources}
                sizes={imgSizes}
              />
              {image?.contentLink?.url && (
                <IconButton
                  sx={{
                    position: 'absolute',
                    right: '16px',
                    bottom: '16px',
                    color: colors.primary.mainGreen,
                  }}
                  onClick={() => handleImageClick(index)}
                >
                  <ZoomOutMapIcon fontSize='inherit' />
                </IconButton>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
        <GalleryModal />
      </ImageGalleryBox>
    </GalleryContext.Provider>
  );
};
